export const DEFAULT_LIST_PAGE_LIMIT = 50;

const toNonNegativeInt = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) return null;
  return Math.floor(number);
};

/**
 * Normalizes a skip/limit request before it is sent to the API.
 * @param {{ skip?: number, limit?: number }} [params]
 * @param {number} [defaultLimit]
 * @returns {{ skip: number, limit: number }}
 */
export function normalizeSkipLimitRequest(params = {}, defaultLimit = DEFAULT_LIST_PAGE_LIMIT) {
  const skip = toNonNegativeInt(params.skip) ?? 0;
  const limit = toNonNegativeInt(params.limit) || defaultLimit;
  return { skip, limit };
}

/**
 * Reads skip/limit/returned pagination.
 * Another page exists only when the API sent pagination and this page was full.
 *
 * @param {Object} [options]
 * @param {Object|null} [options.pagination]
 * @param {number} [options.itemCount]
 * @param {number} [options.requestedSkip]
 * @param {number} [options.requestedLimit]
 * @returns {{ skip: number, limit: number, returned: number, hasMore: boolean, nextSkip: number }}
 */
export function resolveSkipLimitPagination({
  pagination,
  itemCount = 0,
  requestedSkip = 0,
  requestedLimit = DEFAULT_LIST_PAGE_LIMIT,
} = {}) {
  const hasPagination =
    pagination != null && typeof pagination === 'object' && !Array.isArray(pagination);
  const safeRequestedSkip = toNonNegativeInt(requestedSkip) ?? 0;
  const safeRequestedLimit = toNonNegativeInt(requestedLimit) || DEFAULT_LIST_PAGE_LIMIT;
  const safeItemCount = toNonNegativeInt(itemCount) ?? 0;

  const returned = hasPagination
    ? (toNonNegativeInt(pagination.returned) ?? safeItemCount)
    : safeItemCount;
  const limit = hasPagination
    ? (toNonNegativeInt(pagination.limit) || safeRequestedLimit)
    : safeRequestedLimit;
  const skip = hasPagination
    ? (toNonNegativeInt(pagination.skip) ?? safeRequestedSkip)
    : safeRequestedSkip;

  const hasMore = hasPagination && limit > 0 && returned >= limit;

  return {
    skip,
    limit,
    returned,
    hasMore,
    nextSkip: skip + returned,
  };
}

/**
 * Appends incoming rows that are not already present.
 * Rows without an id are kept.
 *
 * @param {Array} existing
 * @param {Array} incoming
 * @param {(item: any) => string|number|null|undefined} getId
 * @returns {Array}
 */
export function mergeUniqueById(existing = [], incoming = [], getId) {
  const base = Array.isArray(existing) ? existing : [];
  if (!Array.isArray(incoming) || incoming.length === 0) {
    return base;
  }

  const seen = new Set();
  base.forEach((item) => {
    const id = getId(item);
    if (id != null && id !== '') seen.add(String(id));
  });

  const merged = base.slice();
  incoming.forEach((item) => {
    const id = getId(item);
    const key = id != null && id !== '' ? String(id) : null;
    if (key && seen.has(key)) return;
    if (key) seen.add(key);
    merged.push(item);
  });

  return merged;
}
